import {
    Box,
    Button,
    Drawer,
    Theme,
    Typography
} from "@mui/material";
import {
    DetailedAccStmtIcon,
    SelectedDetailedAccStmtIcon
} from "../../../assets/SvgIcons";
import { useState } from "react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";


const statementMap: Array<{
    value: string;
    label: string;
    icon: JSX.Element;
    selectedIcon: JSX.Element;
}> = [
        {
            value: "detailedAccountStmt",
            label: "Consolidated Detailed Account Statement",
            icon: <DetailedAccStmtIcon />,
            selectedIcon: <SelectedDetailedAccStmtIcon />
        },
        // {
        //     value: "capitalGainsStmt",
        //     label: "Consolidated Capital Gains Statement",
        //     icon: <CapGainsStmtIcon />,
        //     selectedIcon: <SelectedCapGainsStmtIcon />
        // }
    ]

export function StmtSelection({
    selectedStmt,
    handleStmtSelection,
    isDownloading
}: {
    selectedStmt: string;
    handleStmtSelection: (stmt: string) => void;
    isDownloading: boolean;
}): JSX.Element {
    const { panNo: applicantPanNo = "", } = useSelector((store: RootStateType) => store.id)

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                paddingLeft: "16px "
            }}
        >
            {statementMap.map(({ value, label, icon, selectedIcon }) => (
                <Button
                    key={value}
                    sx={(theme: Theme) => ({
                        alignItems: "center",
                        justifyContent: "flex-start",
                        columnGap: "15px",
                        paddingY: "25px",
                        borderBottom: `1px solid ${theme.palette.grey[100]}`,
                        borderRadius: "0px",
                        backgroundColor: theme.palette.common.white,
                        '&:hover': {
                            backgroundColor: theme.palette.common.white
                        },
                        '&:disabled': {
                            cursor: 'not-allowed',
                            backgroundColor: theme.palette.grey[50]
                        }
                    })}
                    onClick={() => handleStmtSelection(value)}
                    disabled={isDownloading}
                >
                    <Box
                        sx={{
                            flexShrink: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        {(value === selectedStmt) ? selectedIcon : icon}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            rowGap: "5px"
                        }}
                    >
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                letterSpacing: "0.02px",
                                lineHeight: "20px",
                                color: (value === selectedStmt) ? theme.palette.primary[500] : theme.palette.text.secondary,
                                textAlign: "left"
                            })}
                        >
                            {label}
                        </Typography>
                        <Typography
                            component="span"
                            variant='subtitle_500'
                            sx={(theme: Theme) => ({
                                fontWeight: 400,
                                color: `${theme.palette.text.secondary}B2`,
                                textAlign: "left",
                                lineHeight: "20px",
                            })}
                        >
                            {/* Statement Downloaded at the PAN Level :{" "} */}
                            Statement at the PAN Level :{" "}
                            <Typography
                                component="span"
                                variant='subtitle_500'
                                sx={{ fontWeight: 600 }}
                            >
                                {applicantPanNo ?? ""}
                            </Typography>
                        </Typography>
                    </Box>
                </Button>
            ))}
        </Box >
    )
}

export function SmallScreenStmtSelection({
    selectedStmt,
    handleStmtSelection,
    isDownloading
}: {
    selectedStmt: string;
    handleStmtSelection: (stmt: string) => void;
    isDownloading: boolean;
}): JSX.Element {
    const { panNo: applicantPanNo = "", } = useSelector((store: RootStateType) => store.id)

    const [isDrawerOpen, setDrawerOpen] = useState<boolean>(false);

    const onDrawerOpen = () => {
        if (!isDownloading) {
            setDrawerOpen(true)
        }
    }

    const onDrawerClose = () => {
        setDrawerOpen(false)
    }

    const handleDrawerStmtSelection = (stmt: string) => {
        handleStmtSelection(stmt)
        onDrawerClose()
    }

    const selectedStmtIcon = (statementMap ?? []).find(obj => obj.value === selectedStmt)?.selectedIcon ?? ""
    const selectedStmtLabel = (statementMap ?? []).find(obj => obj.value === selectedStmt)?.label ?? ""

    return (
        <>
            <Box
                sx={(theme: Theme) => ({
                    p: '15px',
                    background: theme.palette.common.white,
                    borderRadius: '25px',
                    border: `1px solid ${theme.palette.grey[100]}`,
                    cursor: 'pointer',
                    opacity: 1
                })}
                onClick={onDrawerOpen}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: '8px'
                        }}
                    >
                        <Box
                            sx={{
                                flexShrink: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            }}
                        >
                            {selectedStmtIcon}
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                rowGap: "5px"
                            }}
                        >
                            <Typography
                                variant='subtitle_500'
                                sx={(theme: Theme) => ({
                                    color: theme.palette.primary[500],
                                    letterSpacing: '0.02px'
                                })}
                            >
                                {selectedStmtLabel}
                            </Typography>
                            <Typography
                                component="span"
                                variant='subtitle_500'
                                sx={(theme: Theme) => ({
                                    fontWeight: 400,
                                    color: `${theme.palette.text.secondary}B2`
                                })}
                            >
                                {/* Statement Downloaded at the PAN Level :{" "} */}
                                Statement at the PAN Level :{" "}
                                <Typography
                                    component="span"
                                    variant='subtitle_500'
                                    sx={{ fontWeight: 600 }}
                                >
                                    {applicantPanNo ?? ""}
                                </Typography>
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            flexShrink: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <ExpandMoreIcon />
                    </Box>
                </Box>
            </Box>
            {isDrawerOpen && (
                <Drawer
                    anchor='bottom'
                    open={isDrawerOpen}
                    onClose={onDrawerClose}
                    sx={{
                        ".MuiPaper-elevation": {
                            borderRadius: '25px 25px 0px 0px',
                            padding: '20px',
                            display: "flex",
                            flexDirection: "column",
                        }
                    }}
                >
                    {statementMap.map(({ value, label, icon, selectedIcon }) => (
                        <Button
                            key={value}
                            sx={(theme: Theme) => ({
                                alignItems: "center",
                                justifyContent: "flex-start",
                                columnGap: "8px",
                                borderRadius: "0px",
                                backgroundColor: theme.palette.common.white,
                                '&:hover': {
                                    backgroundColor: theme.palette.common.white
                                },
                                '&:disabled': {
                                    cursor: 'not-allowed',
                                    backgroundColor: theme.palette.grey[50]
                                }
                            })}
                            onClick={() => handleDrawerStmtSelection(value)}
                            disabled={value === "capitalGainsStmt"}
                        >
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            >
                                {(value === selectedStmt) ? selectedIcon : icon}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start"
                                }}
                            >
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        letterSpacing: "0.02px",
                                        color: (value === selectedStmt) ? theme.palette.primary[500] : theme.palette.text.secondary,
                                    })}
                                >
                                    {label}
                                </Typography>
                                <Typography
                                    component="span"
                                    variant='subtitle_500'
                                    sx={(theme: Theme) => ({
                                        fontWeight: 400,
                                        color: `${theme.palette.text.secondary}B2`
                                    })}
                                >
                                    {/* Statement Downloaded at the PAN Level :{" "} */}
                                    Statement at the PAN Level :{" "}
                                    <Typography
                                        component="span"
                                        variant='subtitle_500'
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {applicantPanNo ?? ""}
                                    </Typography>
                                </Typography>
                            </Box>
                        </Button>
                    ))}
                </Drawer>
            )}
        </>
    )
}